// src/features/inventory/hooks/useItemFiltering.js
import { useState, useEffect, useCallback } from 'react';
import { itemsAPI } from '../services/items';
import { useApi } from '../../../hooks/api/useApi';

const DEBOUNCE_MS = 400;

/**
 * Hook for server-side item filtering via POST /api/v1/inventory/items/filter.
 * Manages all filter state and debounces the search term to avoid excessive requests.
 */
export function useItemFiltering(initialState = {}) {
  const { loading, error, execute, clearError } = useApi();

  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm || '');
  const [debouncedSearch, setDebouncedSearch] = useState(initialState.searchTerm || '');
  const [productGroupId, setProductGroupId] = useState(initialState.productGroupId || '');
  const [supplierId, setSupplierId] = useState(initialState.supplierId || '');
  const [activeStatus, setActiveStatus] = useState(initialState.activeStatus || 'all');

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Debounce the search term
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadItems = useCallback(async () => {
    const body = {};
    if (debouncedSearch.trim()) body.search_term = debouncedSearch.trim();
    if (productGroupId) body.product_group_ids = [productGroupId];
    if (supplierId) body.supplier_ids = [supplierId];
    if (activeStatus === 'active') body.active = true;
    else if (activeStatus === 'inactive') body.active = false;

    const result = await execute(() => itemsAPI.filterItems(body));
    if (result.success) {
      const data = result.data?.data;
      setItems(data?.items || []);
      setTotalCount(data?.total_count ?? 0);
    }
  }, [debouncedSearch, productGroupId, supplierId, activeStatus, execute]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const hasActiveFilters =
    searchTerm !== '' || productGroupId !== '' || supplierId !== '' || activeStatus !== 'all';

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setProductGroupId('');
    setSupplierId('');
    setActiveStatus('all');
  }, []);

  return {
    items,
    totalCount,
    loading,
    error,
    clearError,
    searchTerm,
    productGroupId,
    supplierId,
    activeStatus,
    hasActiveFilters,
    setSearchTerm,
    setProductGroupId,
    setSupplierId,
    setActiveStatus,
    clearFilters,
    refreshData: loadItems,
  };
}
